import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  TransformWrapper,
  TransformComponent,
  useControls,
} from "react-zoom-pan-pinch";
import "../styles/InvestmentMap.css"; // Make sure this path is correct
import CityMapBackground from "./CityMapBackground";
import CitySelectorDropdown from "./CitySelectorDropdown";
import KosovoMap from "./KosovoMap";
import { useTranslation } from "react-i18next";

// Constants
const STATUS_COLORS = {
  available: "#28a745",
  rented: "#f7b500",
  sold: "#f7b500", // Assuming sold has same color as rented for legend
};
const MARKER_RADIUS = 10;
const LINE_EXTENSION_SPACE = 150;
const VIEWBOX_PADDING = 0.05;
const LINE_BOTTOM_MARGIN = 10;

// Helper Components
const MapLegend = ({ type }) => {
  const { t } = useTranslation();
  return (
    <div className={`map-legend ${type}-legend`}>
      {type === "city" ? (
        <>
          <div className="legend-item">
            <span className="legend-color-box status-rented-sold"></span>{" "}
            {t("investmentMap.legend.city.rentedSold")}
          </div>
          <div className="legend-item">
            <span className="legend-color-box status-available"></span>{" "}
            {t("investmentMap.legend.city.available")}
          </div>
        </>
      ) : (
        <>
          <div className="legend-item">
            <span
              className="legend-color-box region-available-legend"
              id="box"
            ></span>{" "}
            {t("investmentMap.legend.kosovo.opportunities")}
          </div>
          <div className="legend-item">
            <span
              className="legend-color-box region-other-legend"
              id="other"
            ></span>{" "}
            {t("investmentMap.legend.kosovo.otherRegions")}
          </div>
        </>
      )}
    </div>
  );
};

const Controls = () => {
  const { t } = useTranslation();
  const { zoomIn, zoomOut, resetTransform } = useControls();
  return (
    <div className="map-zoom-controls">
      <button onClick={() => zoomIn(0.2)} title={t("investmentMap.controls.zoomIn")}>
        +
      </button>
      <button onClick={() => zoomOut(0.2)} title={t("investmentMap.controls.zoomOut")}>
        -
      </button>
      <button onClick={() => resetTransform(200)} title={t("investmentMap.controls.resetView")}>
        <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 6v3l4-4-4-4v3c-4.42 0-8 3.58-8 8 0 1.57.46 3.03 1.24 4.26L6.7 14.8c-.45-.83-.7-1.79-.7-2.8 0-3.31 2.69-6 6-6zm6.76 1.74L17.3 9.2c.44.84.7 1.79.7 2.8 0 3.31-2.69 6-6 6v-3l-4 4 4 4v-3c4.42 0 8-3.58 8-8 0-1.57-.46-3.03-1.24-4.26z" />
        </svg>
      </button>
    </div>
  );
};

const PropertyMarker = ({
  property,
  baseCoords,
  index,
  total,
  viewBox,
  currentScale,
  selectedId,
  onClick,
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const { id, name, status } = property; // Assuming 'name' is the field for marker title from backend

  const displayName =
    typeof name === "object" && name !== null
      ? name[currentLanguage] || name.en || name.sq || t("investmentMap.marker.defaultName", { id })
      : name || t("investmentMap.marker.defaultName", { id });

  const offsetSpread = total > 1 ? (MARKER_RADIUS * 1.8) / currentScale : 0;
  const scaledRadius = Math.max(MARKER_RADIUS / currentScale, 3);
  const scaledStrokeWidth = Math.max(1.8 / currentScale, 0.5);

  let cx = baseCoords.x;
  let cy = baseCoords.y;
  if (total > 1) {
    const angle = (index / total) * 2 * Math.PI;
    cx += offsetSpread * Math.cos(angle);
    cy += offsetSpread * Math.sin(angle);
  }

  const vb = parseViewBox(viewBox);
  const lineEndPointY = vb.y + vb.height - LINE_BOTTOM_MARGIN / currentScale;
  const lineEndX = baseCoords.x;

  return (
    <g
      id={`marker-group-${id}`}
      className="property-marker-group"
      onClick={(e) => { e.stopPropagation(); onClick(property); }}
      style={{ cursor: "pointer" }}
    >
      <line
        x1={cx} y1={cy} x2={lineEndX} y2={lineEndPointY}
        className="marker-line" stroke="rgba(255, 255, 255, 0.4)" strokeWidth={scaledStrokeWidth}
      />
      <circle
        cx={cx} cy={cy} r={scaledRadius}
        fill={STATUS_COLORS[status?.toLowerCase()] || "#808080"}
        stroke="#1A1D24" strokeWidth={scaledStrokeWidth / 2}
        className={`property-circle ${getStatusClass(status)} ${selectedId === id ? "selected" : ""}`}
      >
        <title>{displayName}</title>
      </circle>
    </g>
  );
};

const parseViewBox = (vbString) => {
  try {
    const parts = vbString.split(" ").map(Number);
    if (parts.length === 4 && parts.every(isFinite)) {
      return { x: parts[0], y: parts[1], width: parts[2], height: parts[3] };
    }
  } catch (e) { console.error("Error parsing viewBox:", e); }
  return { x: 0, y: 0, width: 1000, height: 1000 };
};

const getStatusClass = (status) => {
  const s = status?.toLowerCase();
  return s === "rented" || s === "sold" ? "status-rented-sold"
       : s === "available" ? "status-available"
       : "status-other";
};

const InvestmentMap = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const [allProperties, setAllProperties] = useState([]);
  const [allMaps, setAllMaps] = useState([]);
  const [selectedCityMapId, setSelectedCityMapId] = useState(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [calculatedViewBox, setCalculatedViewBox] = useState("0 0 1000 1000");
  const [isViewBoxReady, setIsViewBoxReady] = useState(false);

  const transformWrapperRef = useRef(null);
  const mapContentRef = useRef(null);
  const navigate = useNavigate();

  const isCityMapView = selectedCityMapId !== null;

  const currentMapData = useMemo(
    () => (isCityMapView ? allMaps.find((m) => m.id === selectedCityMapId) : null),
    [allMaps, selectedCityMapId, isCityMapView]
  );

  const propertiesForCurrentList = useMemo(
    () =>
      isCityMapView
        ? allProperties.filter((p) => p?.locationId === selectedCityMapId)
        : allProperties,
    [allProperties, selectedCityMapId, isCityMapView]
  );

  const propertiesGroupedByCoords = useMemo(() => {
    if (!isCityMapView || !currentMapData || !propertiesForCurrentList.length) return {};
    return propertiesForCurrentList.reduce((acc, prop) => {
      if (prop?.coordinates && typeof prop.coordinates.x === "number") {
        const coordKey = `${prop.coordinates.x}-${prop.coordinates.y}`;
        if (!acc[coordKey]) acc[coordKey] = { baseCoords: prop.coordinates, props: [] };
        acc[coordKey].props.push(prop);
      }
      return acc;
    }, {});
  }, [isCityMapView, currentMapData, propertiesForCurrentList]);

  const locationIdsWithProperties = useMemo(
    () => new Set(allProperties.map((p) => p?.locationId).filter(Boolean)),
    [allProperties]
  );

  const currentLocationName = useMemo(() => {
    if (isCityMapView && currentMapData) {
      if (typeof currentMapData.name === 'object' && currentMapData.name !== null) {
        return currentMapData.name[currentLanguage] || currentMapData.name.en || currentMapData.name.sq || t("investmentMap.unknownLocation");
      }
      return currentMapData.name || t("investmentMap.unknownLocation");
    }
    return t("investmentMap.kosovoLocationName");
  }, [isCityMapView, currentMapData, currentLanguage, t]);

  useEffect(() => {
    const BACKEND_URL = "https://ddmwrl2uut5q8.cloudfront.net";
    const fetchData = async () => {
      try {
        const [propertyData, mapData] = await Promise.all([
          fetch(`${BACKEND_URL}/api/properties`).then((res) => res.json()),
          fetch(`${BACKEND_URL}/api/maps`).then((res) => res.json()),
        ]);
        setAllProperties(Array.isArray(propertyData) ? propertyData : []);
        setAllMaps(Array.isArray(mapData) ? mapData : []);
      } catch (err) {
        console.error("Error fetching data:", err);
        setAllProperties([]); setAllMaps([]);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    setIsViewBoxReady(false);
    const calculateViewBox = () => {
      if (!mapContentRef.current) {
        setCalculatedViewBox("0 0 1000 1000"); setIsViewBoxReady(true); return;
      }
      try {
        const bbox = mapContentRef.current.getBBox();
        if (bbox.width > 0 && bbox.height > 0 && isFinite(bbox.x) && isFinite(bbox.y)) {
          const paddingX = bbox.width * VIEWBOX_PADDING;
          const paddingY = bbox.height * VIEWBOX_PADDING;
          let vbX = bbox.x - paddingX; let vbY = bbox.y - paddingY;
          let vbWidth = bbox.width + 2 * paddingX; let vbHeight = bbox.height + 2 * paddingY;
          if (isCityMapView && Object.keys(propertiesGroupedByCoords).length > 0) {
            vbHeight += LINE_EXTENSION_SPACE;
          }
          setCalculatedViewBox(`${vbX} ${vbY} ${vbWidth} ${vbHeight}`);
        } else { setCalculatedViewBox("0 0 1000 1000"); }
      } catch (err) {
        console.error("BBox error or map content not ready:", err);
        setCalculatedViewBox("0 0 1000 1000");
      } finally { setIsViewBoxReady(true); }
    };
    const timer = setTimeout(() => {
      requestAnimationFrame(() => requestAnimationFrame(calculateViewBox));
    }, 100);
    return () => clearTimeout(timer);
  }, [selectedCityMapId, propertiesGroupedByCoords, isCityMapView, allMaps]);

  useEffect(() => {
    if (isViewBoxReady && transformWrapperRef.current?.resetTransform) {
      const timer = setTimeout(() => {
        transformWrapperRef.current.resetTransform(200);
        setSelectedPropertyId(null);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [selectedCityMapId, isViewBoxReady]);

  const handleMapChange = useCallback((mapId) => {
    const targetMapExists = mapId === null || allMaps.some((m) => m.id === mapId);
    if (!targetMapExists && mapId !== null) {
      console.warn(`Map ID ${mapId} not found. Returning to Kosovo view.`);
      setSelectedCityMapId(null); return;
    }
    if (mapId !== selectedCityMapId) setSelectedCityMapId(mapId);
  }, [allMaps, selectedCityMapId]);

  const handlePropertyFocus = useCallback((property) => {
    if (!property?.id) return;
    const hasValidCoords = property.coordinates && typeof property.coordinates.x === "number" && typeof property.coordinates.y === "number";
    if (!isCityMapView || !hasValidCoords) {
      navigate(`/properties/${property.id}`); return;
    }
    setSelectedPropertyId(property.id);
    if (transformWrapperRef.current && currentMapData) {
      const { zoomToElement } = transformWrapperRef.current;
      const currentTransformState = transformWrapperRef.current.state;
      const targetElementId = `marker-group-${property.id}`;
      const targetScale = Math.max(currentTransformState?.scale || 1, currentMapData.defaultZoom || 2.5, 2);
      const animationTime = 600;
      if (zoomToElement) {
        zoomToElement(targetElementId, targetScale, animationTime, "easeOut");
      } else { console.warn("zoomToElement not available."); }
      setTimeout(() => navigate(`/properties/${property.id}`), animationTime + 50);
    } else { navigate(`/properties/${property.id}`); }
  }, [isCityMapView, currentMapData, navigate]); // Removed transformWrapperRef from deps, access via .current

  return (
    <div className="investment-map-page">
      <div className="page-header-controls">
        <h2 className="page-title">{t("investmentMap.pageTitle")}</h2>
        <CitySelectorDropdown
          maps={allMaps}
          currentMapId={selectedCityMapId}
          currentMapName={currentLocationName}
          onMapChange={handleMapChange}
          currentLanguage={currentLanguage}
        />
      </div>
      <div className="investment-map-layout">
        <div className="map-area-wrapper">
          <MapLegend type={isCityMapView ? "city" : "kosovo"} />
          {isCityMapView && (
            <button
              className="back-to-main-map-button"
              onClick={() => handleMapChange(null)}
              title={t("investmentMap.backToKosovoTooltip")}
            >
              {t("investmentMap.backToKosovoButton")}
            </button>
          )}
          <TransformWrapper
            ref={transformWrapperRef}
            initialScale={1} minScale={1} maxScale={15} limitToBounds={true}
            doubleClick={{ disabled: true }}
            wheel={{ step: 0.1, smoothStep: 0.005, disabled: false }}
            pan={{ disabled: false }}
          >
            {({ state: currentTransformState }) => (
              <>
                <Controls />
                <TransformComponent
                  wrapperStyle={{ width: "100%", height: "100%", backgroundColor: "#1a1d24" }}
                  contentStyle={{ width: "100%", height: "100%" }}
                  contentClass="map-svg-container"
                >
                  <svg
                    viewBox={isViewBoxReady ? calculatedViewBox : "0 0 1000 1000"}
                    className="map-svg" preserveAspectRatio="xMidYMid meet"
                  >
                    <g ref={mapContentRef} key={selectedCityMapId || "kosovo-map-root"}>
                      {isCityMapView && currentMapData ? (
                        <CityMapBackground paths={currentMapData.paths || []} />
                      ) : (
                        <KosovoMap
                          allMaps={allMaps}
                          locationIdsWithProperties={locationIdsWithProperties}
                          onRegionClick={handleMapChange}
                          currentLanguage={currentLanguage}
                        />
                      )}
                    </g>
                    {isCityMapView && isViewBoxReady &&
                      Object.values(propertiesGroupedByCoords).map(
                        ({ baseCoords, props: groupProps }) =>
                          groupProps.map((property, index) => (
                            <PropertyMarker
                              key={`${property.id}-${index}`}
                              property={property} baseCoords={baseCoords} index={index}
                              total={groupProps.length} viewBox={calculatedViewBox}
                              currentScale={currentTransformState?.scale || 1}
                              selectedId={selectedPropertyId} onClick={handlePropertyFocus}
                            />
                          ))
                      )}
                  </svg>
                </TransformComponent>
              </>
            )}
          </TransformWrapper>
        </div>
        <div className="property-list-sidebar">
          <div className="sidebar-header">
            <h3>{t("investmentMap.sidebar.title", { locationName: currentLocationName })}</h3>
          </div>
          <ul className="property-list">
            {propertiesForCurrentList.length > 0 ? (
              propertiesForCurrentList.map((prop) => {
                const displayTitle =
                  typeof prop.title === "object" && prop.title !== null
                    ? prop.title[currentLanguage] || prop.title.en || prop.title.sq || t("investmentMap.listing.defaultTitle", { id: prop.id })
                    : prop.title || t("investmentMap.listing.defaultTitle", { id: prop.id });

                const displayStatus = prop.status
                  ? t(`investmentMap.status.${prop.status.toLowerCase().replace(/\s+/g, '_')}`, prop.status.charAt(0).toUpperCase() + prop.status.slice(1))
                  : t("investmentMap.status.unknown");
                return (
                  <li
                    key={prop.id}
                    className={`${selectedPropertyId === prop.id ? "selected" : ""} list-item-${getStatusClass(prop.status)}`}
                    onClick={() => handlePropertyFocus(prop)}
                    role="button" tabIndex={0}
                    onKeyPress={(e) => (e.key === "Enter" || e.key === " ") && handlePropertyFocus(prop)}
                  >
                    <span className="property-title-text">{displayTitle}</span>
                    <span className={`status-tag ${getStatusClass(prop.status)}`}>{displayStatus}</span>
                  </li>
                );
              })
            ) : (
              <li className="no-properties">
                {t("investmentMap.sidebar.noProperties", { locationName: currentLocationName })}
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};
export default InvestmentMap;